import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersApi, goalsApi, reviewsApi, departmentsApi, businessUnitsApi, performanceCycleApi, divisionsApi, gradesApi, categoriesApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Loader2, CheckCircle, Circle, Building2, Filter, X, Users, Download, ChevronLeft, ChevronRight } from 'lucide-react';

const ITEMS_PER_PAGE = 50;

export default function OrganizationStatusPage() {
  const { isHR, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [goals, setGoals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [categories, setCategories] = useState([]);
  const [performanceCycle, setPerformanceCycle] = useState(null);
  
  // Filter states
  const [filterEmployeeId, setFilterEmployeeId] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterUnit, setFilterUnit] = useState('all');
  const [filterDivision, setFilterDivision] = useState('all');
  const [filterGrade, setFilterGrade] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  const [currentPage, setCurrentPage] = useState(1);
  
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, goalsRes, reviewsRes, deptsRes, busRes, divRes, gradeRes, catRes, cycleRes] = await Promise.all([
        usersApi.getAll(),
        goalsApi.getAll({ year: currentYear }),
        reviewsApi.getAll({ year: currentYear }),
        departmentsApi.getAll(),
        businessUnitsApi.getAll(),
        divisionsApi.getAll(),
        gradesApi.getAll(),
        categoriesApi.getAll(),
        performanceCycleApi.get(currentYear),
      ]);
      
      setUsers(usersRes.data.filter(u => u.role === 'employee' || u.role === 'manager'));
      setGoals(goalsRes.data || []);
      setReviews(reviewsRes.data || []);
      setDepartments(deptsRes.data || []);
      setBusinessUnits(busRes.data || []);
      setDivisions(divRes.data || []);
      setGrades(gradeRes.data || []);
      setCategories(catRes.data || []);
      setPerformanceCycle(cycleRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get status for each employee
  const getEmployeeStatus = (userId) => {
    const userGoals = goals.filter(g => g.employee_id === userId);
    const userReview = reviews.find(r => r.employee_id === userId);
    
    // Goal Setting: All goals approved
    const goalSettingComplete = userGoals.length > 0 && userGoals.every(g => g.status === 'approved');
    
    // Mid-Year Review: Has mid-year feedback submitted
    const midYearComplete = userReview?.mid_year_feedback?.general_feedback?.what_went_well ? true : false;
    
    // Year-End Review: Has goal_score calculated
    const yearEndComplete = userReview?.goal_score !== null && userReview?.goal_score !== undefined;
    
    // Calibration: Has calibrated_rating
    const calibrationComplete = userReview?.calibrated_rating !== null && userReview?.calibrated_rating !== undefined;
    
    return {
      goalSetting: goalSettingComplete,
      midYear: midYearComplete,
      yearEnd: yearEndComplete,
      calibration: calibrationComplete,
    };
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    if (filterEmployeeId && !user.employee_number?.toLowerCase().includes(filterEmployeeId.toLowerCase())) {
      return false;
    }
    if (filterDepartment !== 'all' && user.department !== filterDepartment) {
      return false;
    }
    if (filterUnit !== 'all' && user.business_unit !== filterUnit) {
      return false;
    }
    if (filterDivision !== 'all' && user.division !== filterDivision) {
      return false;
    }
    if (filterGrade !== 'all' && user.grade !== filterGrade) {
      return false;
    }
    if (filterCategory !== 'all' && user.category !== filterCategory) {
      return false;
    }
    return true;
  });

  const clearFilters = () => {
    setFilterEmployeeId('');
    setFilterDepartment('all');
    setFilterUnit('all');
    setFilterDivision('all');
    setFilterGrade('all');
    setFilterCategory('all');
  };

  const hasActiveFilters = filterEmployeeId || filterDepartment !== 'all' || filterUnit !== 'all' ||
    filterDivision !== 'all' || filterGrade !== 'all' || filterCategory !== 'all';

  // Reset to page 1 whenever filters change
  useEffect(() => { setCurrentPage(1); }, [filterEmployeeId, filterDepartment, filterUnit, filterDivision, filterGrade, filterCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));
  const paginatedUsers = useMemo(
    () => filteredUsers.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [filteredUsers, currentPage]
  );

  const exportToCSV = () => {
    const headers = ['Name', 'Employee Number', 'Department', 'Unit', 'Division', 'Grade', 'Category', 'Goal Setting', 'Mid-Year Review', 'Year-End Review', 'Calibration'];
    const rows = filteredUsers.map(user => {
      const s = getEmployeeStatus(user.id);
      return [
        user.name || '',
        user.employee_number || '',
        user.department || '',
        user.business_unit || '',
        user.division || '',
        user.grade || '',
        user.category || '',
        s.goalSetting ? 'Done' : 'Pending',
        s.midYear ? 'Done' : 'Pending',
        s.yearEnd ? 'Done' : 'Pending',
        s.calibration ? 'Done' : 'Pending',
      ];
    });
    const csv = [headers, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employee-progress-${currentYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calculate summary statistics
  const summary = {
    total: filteredUsers.length,
    goalSettingComplete: filteredUsers.filter(u => getEmployeeStatus(u.id).goalSetting).length,
    midYearComplete: filteredUsers.filter(u => getEmployeeStatus(u.id).midYear).length,
    yearEndComplete: filteredUsers.filter(u => getEmployeeStatus(u.id).yearEnd).length,
    calibrationComplete: filteredUsers.filter(u => getEmployeeStatus(u.id).calibration).length,
  };

  const StatusIcon = ({ completed }) => (
    completed ? (
      <CheckCircle className="h-5 w-5 text-emerald-500" />
    ) : (
      <Circle className="h-5 w-5 text-gray-300" />
    )
  );

  if (!isHR && !isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Access restricted to HR and Admin only</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="org-status-page">
      <div>
        <h1 className="text-3xl font-bold">Organization Status</h1>
        <p className="text-muted-foreground mt-1">
          Monitor employee progress across all performance cycle stages
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-slate-50">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold">{summary.total}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Employees</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-700">{summary.goalSettingComplete}</p>
              <p className="text-xs text-muted-foreground mt-1">Goal Setting Done</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-700">{summary.midYearComplete}</p>
              <p className="text-xs text-muted-foreground mt-1">Mid-Year Done</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-700">{summary.yearEndComplete}</p>
              <p className="text-xs text-muted-foreground mt-1">Year-End Done</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-700">{summary.calibrationComplete}</p>
              <p className="text-xs text-muted-foreground mt-1">Calibration Done</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto h-7 text-xs">
                <X className="h-3 w-3 mr-1" />
                Clear All
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Employee ID</Label>
              <Input
                placeholder="Search..."
                value={filterEmployeeId}
                onChange={(e) => setFilterEmployeeId(e.target.value)}
                className="h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Department</Label>
              <SearchableSelect
                value={filterDepartment}
                onValueChange={setFilterDepartment}
                placeholder="All"
                searchPlaceholder="Search departments..."
                className="h-9 mt-1"
                options={[
                  { value: "all", label: "All Departments" },
                  ...departments.map(d => ({ value: d.name, label: d.name })),
                ]}
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Unit</Label>
              <SearchableSelect
                value={filterUnit}
                onValueChange={setFilterUnit}
                placeholder="All"
                searchPlaceholder="Search units..."
                className="h-9 mt-1"
                options={[
                  { value: "all", label: "All Units" },
                  ...businessUnits.map(bu => ({ value: bu.name, label: bu.name })),
                ]}
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Division</Label>
              <SearchableSelect
                value={filterDivision}
                onValueChange={setFilterDivision}
                placeholder="All"
                searchPlaceholder="Search divisions..."
                className="h-9 mt-1"
                options={[
                  { value: "all", label: "All Divisions" },
                  ...divisions.map(d => ({ value: d.name, label: d.name })),
                ]}
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Grade</Label>
              <SearchableSelect
                value={filterGrade}
                onValueChange={setFilterGrade}
                placeholder="All"
                searchPlaceholder="Search grades..."
                className="h-9 mt-1"
                options={[
                  { value: "all", label: "All Grades" },
                  ...grades.map(g => ({ value: g.name, label: g.name })),
                ]}
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <SearchableSelect
                value={filterCategory}
                onValueChange={setFilterCategory}
                placeholder="All"
                searchPlaceholder="Search categories..."
                className="h-9 mt-1"
                options={[
                  { value: "all", label: "All Categories" },
                  ...categories.map(c => ({ value: c.name, label: c.name })),
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee Status Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Employee Progress ({currentYear})
            <Badge variant="outline" className="ml-2">{filteredUsers.length} of {users.length}</Badge>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={exportToCSV}
              disabled={filteredUsers.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </CardTitle>
          <CardDescription>
            Track completion status for each stage of the performance cycle
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {users.length === 0 ? 'No employees found' : 'No employees match the selected filters'}
            </div>
          ) : (
            <Table>

              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-center">Goal Setting</TableHead>
                  <TableHead className="text-center">Mid-Year Review</TableHead>
                  <TableHead className="text-center">Year-End Review</TableHead>
                  <TableHead className="text-center">Calibration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.map((user) => {
                  const status = getEmployeeStatus(user.id);
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-medium text-primary">
                              {user.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{user.name}</p>
                            <p className="text-xs text-muted-foreground">{user.employee_number}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{user.department || '-'}</TableCell>
                      <TableCell className="text-sm">{user.business_unit || '-'}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center">
                          <StatusIcon completed={status.goalSetting} />
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center">
                          <StatusIcon completed={status.midYear} />
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center">
                          <StatusIcon completed={status.yearEnd} />
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center">
                          <StatusIcon completed={status.calibration} />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} employees
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm font-medium px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
